import React, { useEffect, useState } from "react";
import { X, Bell } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Email capture popup shown on city landing pages after a short delay.
 * Copy + delay + enabled-state all come from /api/settings/city-popup
 * (admin-editable). Remembers dismissal per-city in localStorage.
 */
export default function EmailCapturePopup({ city }) {
  const [settings, setSettings] = useState(null);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      const key = `loot_popup_${city}`;
      if (localStorage.getItem(key)) return;
      try {
        const { data } = await api.get("/settings/city-popup");
        if (!data.enabled) return;
        setSettings(data);
      } catch {
        /* noop */
      }
    })();
  }, [city]);

  useEffect(() => {
    if (!settings) return;
    const t = setTimeout(() => setOpen(true), (settings.delay_seconds || 0) * 1000);
    return () => clearTimeout(t);
  }, [settings]);

  const dismiss = () => {
    localStorage.setItem(`loot_popup_${city}`, "1");
    setOpen(false);
  };

  const submit = async () => {
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/city-subscribe", { email: email.trim(), city });
      toast.success(`You're on the list for ${city} drops.`);
      dismiss();
    } catch (e) {
      toast.error("Could not subscribe. Try again?");
      setSubmitting(false);
    }
  };

  if (!open || !settings) return null;

  const title = (settings.title || "").replace("{city}", city);
  const sub = (settings.subtitle || "").replace("{city}", city);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/70 backdrop-blur-sm"
      data-testid="city-popup"
    >
      <div
        className="loot-card-flat p-6 m-4 max-w-md w-full space-y-4 relative"
        style={{ borderColor: "#76B82A" }}
      >
        <button
          onClick={dismiss}
          className="absolute top-3 right-3 text-[#F5F2E8]/50 hover:text-[#F5F2E8]"
          aria-label="Close"
          data-testid="city-popup-close"
        >
          <X size={20} />
        </button>
        <div className="flex items-center gap-2">
          <Bell size={16} style={{ color: "#76B82A" }} />
          <span className="overline">Notify me</span>
        </div>
        <h3 className="font-display uppercase text-2xl md:text-3xl tracking-tight leading-tight">
          {title}
        </h3>
        <p className="text-sm text-[#F5F2E8]/70">{sub}</p>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          className="loot-input"
          data-testid="city-popup-email"
          onKeyDown={(e) => e.key === "Enter" && submit()}
        />
        <button
          onClick={submit}
          disabled={submitting || !email.trim()}
          className="btn-loot w-full justify-center"
          data-testid="city-popup-submit"
        >
          {submitting ? "..." : settings.cta || "Notify me"}
        </button>
      </div>
    </div>
  );
}
